import {EventEmitter, Injectable} from '@angular/core';
import {HttpClient, HttpEvent, HttpEventType, HttpRequest, HttpResponse} from '@angular/common/http';
import {catchError, last, map, tap} from 'rxjs/operators';
import {QueueItem} from './queue-item';
import {environment} from '@environments/environment';
import {UploadedFile} from '@interfaces/file.interface';

interface UploadResponse {
    status: boolean;
    data: UploadedFile;
}

@Injectable()
export class FilesUploaderService {

    public updateProgress$ = new EventEmitter();

    constructor(
        private http: HttpClient,
    ) {
    }

    upload(queueItem: QueueItem, uploadTarget: 'Lab'|'Noap'|'Noap_public' = 'Lab') {
        const data = new FormData();
        /**
         * Так как использовать new File мы не можем (ибо в Edge не работает конструктор File), то передаем filename для blob так
         * https://developer.mozilla.org/en-US/docs/Web/API/FormData/append
         */
        data.append('file', queueItem.file, queueItem.name);

        const url = environment.FILE_UPLOAD_URLS[uploadTarget];

        const req = new HttpRequest('POST', url, data, {
            reportProgress: true
        });

        return this.http.request(req).pipe(
            tap((event: HttpEvent<any>) => {
                switch (event.type) {
                    /**
                     * Начали загрузку
                     */
                    case HttpEventType.Sent:
                        queueItem.setProgress(0);
                        this.updateProgress$.emit();
                        break;

                    /**
                     * Загрузка в процессе
                     */
                    case HttpEventType.UploadProgress:
                        // Compute and show the % done:
                        const percentDone = Math.round(100 * event.loaded / event.total);
                        queueItem.setProgress(percentDone);

                        this.updateProgress$.emit();

                        break;

                    /**
                     * Загрузка завершена
                     */
                    case HttpEventType.Response:
                        queueItem.setComplete();
                        this.updateProgress$.emit();
                        break;
                }
            }),
            last(), // return last (completed) message to caller
            map((response: HttpResponse<UploadResponse>) => {
                const body = response.body;

                if (body.status) {
                    queueItem.setComplete();
                    this.updateProgress$.emit();
                    return body.data;
                } else {
                    throw null;
                }
            }),
            catchError(() => {
                queueItem.setError('Не удалось загрузить файл');
                this.updateProgress$.emit();
                const emptyFile: UploadedFile = {
                    file_id: null,
                    file_name: null,
                    file_url: null,
                    file_size: null,
                    file_mime_type: null,
                };
                throw emptyFile;
            })
        );
    }

    /**
     * Преобразовать dataURI в BLOB
     * Нашел где-то на просторах интернета, самому было некогда придумывать
     * @param dataURI
     */
    public dataURItoBlob(dataURI: string) {
        // convert base64/URLEncoded data component to raw binary data held in a string
        let byteString;
        if (dataURI.split(',')[0].indexOf('base64') >= 0) {
            byteString = atob(dataURI.split(',')[1]);
        } else {
            byteString = unescape(dataURI.split(',')[1]);
        }

        // separate out the mime component
        const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

        // write the bytes of the string to a typed array
        const ia = new Uint8Array(byteString.length);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }

        return new Blob([ia], {type: mimeString});
    }
}
